<template>
  <div class="tl">
    <Breadcrumbs main="Рейтинг" title="Мировой рейтинг"/>

    <div class="container-fluid tl-container">

      <div class="tl-toolbar">
        <div class="tl-toolbar__main">
          <div class="tl-search">
            <i class="fa fa-search tl-search__icon"></i>
            <input ref="search"
                   type="search"
                   class="tl-search__input"
                   v-model="searchModel"
                   placeholder="Поиск по названию команды…"
                   @keydown.esc.prevent="clearSearch"/>
            <button v-if="searchModel" type="button" class="tl-search__clear" @click="clearSearch">
              <i class="fa fa-times"></i>
            </button>
            <kbd v-else class="tl-search__kbd">/</kbd>
          </div>

          <!-- регионы: раньше пять одинаковых кнопок без активного состояния -->
          <div v-if="tabList.length" class="tl-seg rt-seg" role="group" aria-label="Регион">
            <button v-for="region in tabList" :key="'region' + region.id"
                    type="button" class="tl-seg__btn rt-seg__btn"
                    :class="{ 'is-on': $route.params.region === region.id }"
                    @click="setRegion(region.id)">
              {{ regionLabel(region) }}
            </button>
          </div>

          <div class="tl-spacer"></div>

          <Actualize :actualize-function="actualizeRating">Актуализировать</Actualize>
        </div>

        <div class="rt-meta">
          <span v-if="currentRegion">Регион: <b>{{ regionLabel(currentRegion) }}</b></span>
          <span v-if="searchApplied" class="rt-meta__chip">
            Поиск: «{{ searchApplied }}»
            <button type="button" @click="clearSearch"><i class="fa fa-times"></i></button>
          </span>
        </div>
      </div>

      <div class="tl-panel">
        <div v-if="isLoading" class="tl-skeleton">
          <div v-for="n in 8" :key="'sk' + n" class="tl-skeleton__row">
            <span class="tl-sk tl-sk--sm"></span>
            <span class="tl-sk tl-sk--wide"></span>
            <span class="tl-sk"></span>
            <span class="tl-sk tl-sk--sm"></span>
          </div>
        </div>

        <div v-else-if="!teamsWorldRatingList.length" class="tl-empty">
          <div class="tl-empty__art"><i class="fa fa-globe"></i></div>
          <div class="tl-empty__title">{{ searchApplied ? 'Ничего не нашлось' : 'Рейтинга нет' }}</div>
          <div class="tl-empty__text">
            {{ searchApplied ? 'Поиск работает по названию команды.' : 'Для этого региона рейтинг ещё не рассчитан.' }}
          </div>
          <div v-if="searchApplied" class="tl-empty__actions">
            <button type="button" class="tl-btn-ghost" @click="clearSearch">Сбросить поиск</button>
          </div>
        </div>

        <div v-else class="tl-table-wrap">
          <b-table class="tl-table rt-table"
                   responsive
                   hover
                   borderless
                   :items="teamsWorldRatingList"
                   :fields="teamsWorldRatingFields">
            <template #cell(place)="{ item }">
              <span class="rt-place" :class="placeClass(item.place)">{{ item.place }}</span>
            </template>

            <template #cell(id)="{ item }">
              <button type="button" class="tl-id" @click.stop="copyId(item.id)"
                      v-b-tooltip.hover.right="'Скопировать ID команды'">
                <span>#{{ item.id }}</span>
                <i class="fa fa-copy"></i>
              </button>
            </template>

            <template #cell(name)="{ item }">
              <router-link class="rt-team__name"
                           :to="{ name: 'teams_info', params: { id: item.id } }">{{ item.name }}</router-link>
            </template>

            <template #cell(rating)="{ item }">
              <span class="rt-rating">{{ formatRating(item.rating) }}</span>
            </template>

            <template #cell(country)="{ item }">
              <span v-if="item.country" class="rt-chip">{{ String(item.country).toUpperCase() }}</span>
              <span v-else class="rt-dim">—</span>
            </template>
          </b-table>
        </div>

        <div v-if="!isLoading && teamsWorldRatingList.length" class="tl-foot">
          <div class="tl-foot__info">
            Страница <b>{{ currentPage }}</b> из <b>{{ paginationTotal || 1 }}</b>
          </div>
          <PaginationRating :total="paginationTotal"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api/api";
import PaginationRating from "@/pages/Rating/components/PaginationRating.vue";
import Actualize from "@/pages/Rating/components/Actualize.vue";
import lodash from "lodash";

export default {
  name: "WorldRatingList",
  components: {
    PaginationRating,
    Actualize,
  },
  data() {
    return {
      tabList: [],
      teamsWorldRatingList: [],
      teamsWorldRatingFields: [
        { key: 'place', label: '#', thClass: 'rt-th--place' },
        { key: 'id', label: 'ID', thClass: 'tl-th--id' },
        { key: 'name', label: 'Команда' },
        { key: 'rating', label: 'Рейтинг', thClass: 'rt-th--num' },
        { key: 'country', label: 'Страна' }
      ],
      searchText: '',
      debouncedSearch: lodash.debounce(function () {
        this.applySearch();
      }, 600),
      paginationTotal: 0,
      isLoading: false, // <-- добавили
    };
  },
  watch: {
    "$route"(newRoute) {
      this.searchText = newRoute.query.search || '';
      if (newRoute.params.region) {
        this.handleQueryChange();
      }
    },
  },
  computed: {
    currentRegion() {
      return (this.tabList || []).find((region) => region.id === this.$route.params.region) || null;
    },

    searchApplied() {
      return (this.$route.query.search || '').trim();
    },

    currentPage() {
      return parseInt(this.$route.query.page) || 1;
    },

    searchModel: {
      get() { return this.searchText },
      set(value) {
        this.searchText = value;
        this.debouncedSearch();
      }
    },
  },
  mounted() {
    this.searchText = this.$route.query.search || '';
    window.addEventListener('keydown', this.focusSearch);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.focusSearch);
  },
  methods: {
    /** Бэкенд отдаёт ключи локализации вида rating.region_cis — показываем человеку название. */
    regionLabel(region) {
      const names = {
        cis: 'СНГ',
        uzbekistan: 'Узбекистан',
        kazakhstan: 'Казахстан',
        kyrgyzstan: 'Кыргызстан',
        tajikistan: 'Таджикистан',
        turkmenistan: 'Туркменистан',
        azerbaijan: 'Азербайджан',
        armenia: 'Армения',
        georgia: 'Грузия',
        belarus: 'Беларусь',
        moldova: 'Молдова',
      };
      const slug = String(region.id || '').toLowerCase();
      if (names[slug]) return names[slug];

      const raw = String(region.name || region.id || '').replace(/^rating\.region_/, '').replace(/_/g, ' ');
      return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : '—';
    },

    focusSearch(event) {
      if (event.key !== '/' || ['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target.tagName)) return;
      event.preventDefault();
      if (this.$refs.search) this.$refs.search.focus();
    },

    applySearch() {
      const query = { ...this.$route.query };
      const value = this.searchText.trim();

      if (value) query.search = value;
      else delete query.search;
      delete query.page;

      if ((this.$route.query.search || '') === (value || '')) return;
      this.$router.push({ path: this.$route.path, query });
    },

    clearSearch() {
      this.searchText = '';
      this.applySearch();
    },

    setRegion(regionId) {
      if (this.$route.params.region === regionId) return;
      this.$router.push({ name: 'world-rating-info', params: { region: regionId } });
    },

    placeClass(place) {
      if (Number(place) === 1) return 'rt-place--gold';
      if (Number(place) === 2) return 'rt-place--silver';
      if (Number(place) === 3) return 'rt-place--bronze';
      return '';
    },

    formatRating(value) {
      return Number(value || 0).toFixed(2);
    },

    copyId(id) {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(String(id)).then(() => {
          this.$toasted.success('ID команды скопирован', {position: 'top-right', duration: 1500});
        }).catch(() => {
        });
      }
    },

    async actualizeRating() {
      try {
        await api.worldTeamRating.actualize();
        console.log("Актуализация завершена");
      } catch (error) {
        console.error("Ошибка актуализации:", error);
      }
    },

    async handleQueryChange() {
      const queryParams = {
        region: this.$route.params.region,
        page: parseInt(this.$route.query.page) || 1,
      };
      if (this.$route.query.search) {
        queryParams['filter[team.name]'] = this.$route.query.search;
      }
      this.fetchTeamsWorldRating(queryParams);
    },

    formatData(data) {
      return Object.entries(data).map(([key, value]) => ({
        id: key,
        name: value,
        slug: key,
      }));
    },

    async fetchTeamsWorldRatingRegions() {
      try {
        const res = await api.worldTeamRating.regions();
        this.tabList = this.formatData(res.data.data);
      } catch (error) {
        console.error("Error fetching world rating regions:", error);
      }
    },

    async fetchTeamsWorldRating(queryParams = {}) {
      this.isLoading = true; // <-- Показать лоаддер
      try {
        this.teamsWorldRatingList = [];
        const { region, ...params } = queryParams;
        const res = await api.worldTeamRating.index(region, params);
        this.paginationTotal = Math.ceil(res.data.pagination.total / res.data.pagination.by);
        this.teamsWorldRatingList = res.data.data.map(team => ({
          id: team.id,
          place: team.place,
          name: team.name,
          rating: team.rating,
          country: team.country.code,
        }));
      } catch (error) {
        console.error("Error fetching world rating:", error);
      } finally {
        this.isLoading = false; // <-- Скрыть лоаддер
      }
    },
  },
  created() {
    if (this.$route.params.region) {
      const queryParams = {
        region: this.$route.params.region,
        page: parseInt(this.$route.query.page) || 1,
      };
      this.fetchTeamsWorldRatingRegions();
      this.fetchTeamsWorldRating(queryParams);
    } else {
      this.$router.replace({
        name: 'world-rating-info',
        params: { region: 'cis' },
        query: this.$route.query,
      });
    }
  }
};
</script>

<style scoped lang="scss">
@import "~@/assets/scss/admin-list.scss";
@import "~@/assets/scss/rating-list.scss";

.tl { @include rating-list-parts; }
</style>
